import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { analyseExpression } from '../face/expression.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Neutral-expression check.
 *
 * Never a hard failure: a smile is a soft note in every document profile,
 * and a model that mistakes a neutral face for a smile must not reject a valid
 * photo. The check therefore only produces pass / warning / uncertain, and it
 * always reports how sure it is.
 */
export const expressionCheck: ValidationCheck = {
  id: 'expression',
  title: 'Нейтральное выражение лица',
  description: 'Проверяет, что лицо нейтральное: без улыбки и без широко открытого рта.',
  kind: 'content',
  requires: ['faces'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['faces']);
    if (missing) return notChecked(ctx, expressionCheck, missing, ctx.rule.severity);

    const maxSmileScore = numberParam(ctx.rule.params, 'maxSmileScore', 0.45);
    const maxJawOpen = numberParam(ctx.rule.params, 'maxJawOpen', 0.5);
    const uncertainBelow = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.6);

    const face = ctx.faces.faces[0];
    if (!face) {
      return notChecked(
        ctx,
        expressionCheck,
        'Лицо не обнаружено: оценить выражение невозможно.',
        ctx.rule.severity,
      );
    }

    const analysis = analyseExpression({
      ...(face.blendshapes ? { blendshapes: face.blendshapes } : {}),
      landmarks: face.landmarks,
      ...(ctx.geometry ? { geometry: ctx.geometry } : {}),
      faceConfidence: face.confidence,
    });

    const smileTrigger = analysis.smileScore > maxSmileScore;
    const jawTrigger = analysis.jawScore > maxJawOpen;
    const triggered = smileTrigger || jawTrigger;
    const confident = analysis.confidence >= uncertainBelow;

    const status: CheckResult['status'] = !triggered
      ? 'pass'
      : confident
        ? 'warning'
        : 'uncertain';

    const message = (() => {
      if (status === 'pass') return '✅ Выражение лица нейтральное.';
      if (status === 'uncertain') return '⚠ Не удалось надёжно оценить выражение лица.';
      if (smileTrigger) {
        return jawTrigger
          ? '⚠ Возможно выражение улыбки, рот приоткрыт.'
          : '⚠ Возможно выражение улыбки.';
      }
      return '⚠ Возможно приоткрытый рот — выражение лица не выглядит нейтральным.';
    })();


    return plainResult({
      id: expressionCheck.id,
      title: expressionCheck.title,
      status,
      severity: ctx.rule.severity,
      value: analysis.neutrality,
      unit: 'ratio',
      ...(ctx.rule.official ? { official: ctx.rule.official } : {}),
      message,
      ...(ctx.rule.official?.statement ? { requirement: ctx.rule.official.statement } : {}),
      fixability: 'retake',
      confidence: analysis.confidence,
      details: {
        smileScore: analysis.smileScore,
        mouthOpenScore: analysis.mouthOpenScore,
        jawScore: analysis.jawScore,
        neutrality: analysis.neutrality,
        blendshapeSource: analysis.source,
        maxSmileScore,
        maxJawOpen,
        uncertainBelowConfidence: uncertainBelow,
        confidence: roundTo(analysis.confidence, 3),
        notes: analysis.notes,
        displayValue: status === 'pass' ? 'нейтральное' : 'возможна улыбка',
      },
    });
  },
};
